// covered-by: frontends/carrie/src/utils/queries/__tests__/askPunttOrgFilters.test.tsx
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as OrgFiltersService from "../../services/askPunttOrgFilters";
import {
  type LauncherSavedView,
  type LauncherSurface,
} from "../../services/askPunttOrgFilters";

export const askPunttOrgKeys = {
  orgLabels: ["ask-puntt-org-labels"] as const,
  launcherViews: ["ask-puntt-launcher-views"] as const,
};

export function useOrgLabels() {
  return useQuery({
    queryKey: askPunttOrgKeys.orgLabels,
    queryFn: () => OrgFiltersService.listOrgLabels(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useLauncherViews(surface?: LauncherSurface) {
  return useQuery({
    queryKey: [...askPunttOrgKeys.launcherViews, surface ?? "all"],
    queryFn: () => OrgFiltersService.listLauncherViews(surface),
    staleTime: 60 * 1000,
  });
}

export function useCreateLauncherView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      surface: LauncherSurface;
      filters: LauncherSavedView["filters"];
      isDefault?: boolean;
    }) => OrgFiltersService.createLauncherView(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: askPunttOrgKeys.launcherViews,
      });
    },
  });
}

export function useUpdateLauncherView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      viewId,
      payload,
    }: {
      viewId: string;
      payload: Partial<{ name: string; filters: LauncherSavedView["filters"] }>;
    }) => OrgFiltersService.updateLauncherView(viewId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: askPunttOrgKeys.launcherViews,
      });
    },
  });
}

export function useDeleteLauncherView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) =>
      OrgFiltersService.deleteLauncherView(viewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: askPunttOrgKeys.launcherViews,
      });
    },
  });
}

export function useUpdateProjectLabels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: string;
      payload: { tags?: string[] };
    }) => OrgFiltersService.updateProjectLabels(projectId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["ask-puntt-projects"],
      });
      void queryClient.invalidateQueries({
        queryKey: askPunttOrgKeys.orgLabels,
      });
    },
  });
}

export function useUpdateChatLabels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      chatId,
      payload,
    }: {
      chatId: string;
      payload: { tags?: string[] };
    }) => OrgFiltersService.updateChatLabels(chatId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["ask-puntt"],
      });
      void queryClient.invalidateQueries({
        queryKey: askPunttOrgKeys.orgLabels,
      });
    },
  });
}

export function useUpdateFileLabels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      fileId,
      payload,
    }: {
      projectId: string;
      fileId: string;
      payload: { tags?: string[] };
    }) => OrgFiltersService.updateFileLabels(projectId, fileId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["ask-puntt-projects"],
      });
      void queryClient.invalidateQueries({
        queryKey: askPunttOrgKeys.orgLabels,
      });
    },
  });
}
